//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { ScheduledPostTimeUpdate } from '@bvengineering/universal-scheduling-gateway-service-types';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { Moment } from 'moment-timezone';

import {
  isInstagramFeedAutomaticPostConfig,
  isInstagramFeedManualPostConfig,
} from '@apis/universal-posts/config/universalScheduledPostsConfigUtils';
import * as universalScheduledPostsApi from '@apis/universal-posts/scheduled-posts/universalScheduledPostsApi';
import { UniversalPostingGroupName } from '@apis/universal-posts/universalPostsApiModel';
import * as userSettingsApi from '@apis/user-settings/userSettingsApi';
import * as instagramScheduleFeedPreviewActions from '@instagram-account/redux/schedule/feed-preview/instagramScheduleFeedPreviewSlice';
import {
  IInstagramScheduleState,
  InstagramScheduleNavigationState,
  InstagramSchedulePostFilter,
  InteractiveCalendarSettings,
  NormalizedScheduledPosts,
  SCHEDULE_FETCH_LIMIT,
} from '@instagram-account/redux/schedule/instagramScheduleModel';
import {
  addBatchReschedulingGenericFailureToast,
  addBatchReschedulingSuccessToast,
  addReschedulingFailureToast,
  addReschedulingSuccessToast,
  addTimeInPastFailureToast,
} from '@instagram-account/redux/schedule/instagramScheduleToasts';
import {
  getMonthViewStartDate,
  getNextStartDate,
  getPreviousStartDate,
  getStartOfCurrentMonth,
  getTemporaryScheduledPosts,
  getTemporaryScheduledPostsForBatchReschedule,
  getTimezonedMomentFromStartDateOffset,
  getWeekViewStartDate,
  isInstagramScheduledPost,
  isScheduledFeedPreviewItem,
  mergeScheduledItemLists,
  sortScheduledPosts,
  toStartAndEndMoment,
} from '@instagram-account/redux/schedule/instagramScheduleUtils';
import { getCurrentStartDate } from '@instagram-account/redux/schedule/selectors/getCurrentStartDate';
import { getFeedPreviewItems } from '@instagram-account/redux/schedule/selectors/getFeedPreviewItems';
import DateString from '@models/DateString';
import PostableId from '@models/PostableId';
import PostableStatus from '@models/PostableStatus';
import TimeZoneId from '@models/TimeZoneId';
import { UniversalPostableCustomStatus } from '@models/UniversalPostableCustomStatus';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';
import { extractAllUniversalScheduledPostMedia } from '@redux/schedule-media/instagramScheduleMediaUtils';
import * as scheduleMediaActions from '@redux/schedule-media/scheduleMediaSlice';
import { getUserTimeZone } from '@redux/selectors/getUserTimeZone';
import { UniversalCalendarView } from '@universal-scheduling/redux/calendar/universalCalendarModel';
import { move } from '@utils/arrayUtils';
import { isDefined } from '@utils/filterPredicates';
import { getRequestErrorMessage } from '@utils/getRequestErrorMessage';
import { momentFromDate } from '@utils/moment';
import normalize, { Normalized } from '@utils/normalize';

const initialState: IInstagramScheduleState = {
  errors: {
    fetchScheduledPostsError: undefined,
    reschedulePostError: undefined,
  },
  flags: {
    isSheetOpen: false,
    isFetchingInteractiveCalendarSettings: false,
    isUpdatingInteractiveCalendarSettings: false,
    isFetchingScheduledPosts: false,
    isInitialScheduleDataLoaded: false,
    isReschedulingPost: false,
  },
  scheduledPosts: Normalized.empty(),
  // We set an initial startDate so our selectors don't explode,
  // but this will be overwritten in the initial setup call.
  startDate: DateString.getToday(TimeZoneId.Utc),
  view: UniversalCalendarView.Week,
  captionSearchText: undefined,
  startOfWeekDay: 0,
  postFilter: 'all',
  navigationReturnDate: undefined,
  navigationState: 'none',
};

const { actions, reducer } = createSlice({
  name: SliceNames.InstagramAccountSchedule,
  initialState,
  reducers: {
    setInitialScheduleData(
      state,
      action: PayloadAction<{
        scheduledPosts: NormalizedScheduledPosts;
        startDate: DateString;
        view: UniversalCalendarView;
        fetchScheduledPostsError?: string;
      }>,
    ) {
      state.flags.isInitialScheduleDataLoaded = true;
      state.scheduledPosts = action.payload.scheduledPosts;
      state.startDate = action.payload.startDate;
      state.view = action.payload.view;
      state.errors.fetchScheduledPostsError =
        action.payload.fetchScheduledPostsError;
    },
    requestScheduledPosts(state) {
      state.errors.fetchScheduledPostsError = undefined;
      state.flags.isFetchingScheduledPosts = true;
    },
    receiveScheduledPostsSucceeded(
      state,
      action: PayloadAction<NormalizedScheduledPosts>,
    ) {
      state.flags.isFetchingScheduledPosts = false;
      state.scheduledPosts = action.payload;
    },
    receiveScheduledPostsFailed(
      state,
      action: PayloadAction<string | undefined>,
    ) {
      state.flags.isFetchingScheduledPosts = false;
      state.errors.fetchScheduledPostsError = action.payload;
    },
    requestReschedulePost(
      state,
      action: PayloadAction<NormalizedScheduledPosts>,
    ) {
      state.errors.reschedulePostError = undefined;
      state.flags.isReschedulingPost = true;
      state.scheduledPosts = action.payload;
    },
    receiveReschedulePostSucceeded(
      state,
      action: PayloadAction<NormalizedScheduledPosts>,
    ) {
      state.flags.isReschedulingPost = false;
      state.scheduledPosts = action.payload;
    },
    receiveReschedulePostFailed(
      state,
      action: PayloadAction<{
        errorMsg?: string;
        scheduledPosts: NormalizedScheduledPosts;
      }>,
    ) {
      state.errors.reschedulePostError = action.payload.errorMsg;
      state.flags.isReschedulingPost = false;
      state.scheduledPosts = action.payload.scheduledPosts;
    },
    requestInteractiveCalendarSettings(state) {
      state.errors.fetchInteractiveCalendarSettingsError = undefined;
      state.flags.isFetchingInteractiveCalendarSettings = true;
    },
    receiveInteractiveCalendarSettingsSucceeded(
      state,
      action: PayloadAction<InteractiveCalendarSettings>,
    ) {
      const { scheduleCalendarStartOfWeek } = action.payload;
      state.startOfWeekDay = scheduleCalendarStartOfWeek ?? 0;
      state.flags.isFetchingInteractiveCalendarSettings = false;
    },
    receiveInteractiveCalendarSettingsFailed(
      state,
      action: PayloadAction<string | undefined>,
    ) {
      state.errors.fetchInteractiveCalendarSettingsError = action.payload;
      state.flags.isFetchingInteractiveCalendarSettings = false;
    },
    requestUpdateInteractiveCalendarSettings(state) {
      state.errors.updateInteractiveCalendarSettingsError = undefined;
      state.flags.isUpdatingInteractiveCalendarSettings = true;
    },
    receiveUpdateInteractiveCalendarSettingsSucceeded(state) {
      state.flags.isUpdatingInteractiveCalendarSettings = false;
    },
    receiveUpdateInteractiveCalendarSettingsFailed(
      state,
      action: PayloadAction<string | undefined>,
    ) {
      state.errors.updateInteractiveCalendarSettingsError = action.payload;
      state.flags.isUpdatingInteractiveCalendarSettings = false;
    },
    setScheduleView(state, action: PayloadAction<UniversalCalendarView>) {
      state.view = action.payload;
    },
    setPostFilter(state, action: PayloadAction<InstagramSchedulePostFilter>) {
      state.postFilter = action.payload;
    },
    setCaptionSearchText(state, action: PayloadAction<string>) {
      state.captionSearchText = action.payload;
    },
    clearCaptionSearchText(state) {
      state.captionSearchText = undefined;
    },
    setStartDate(state, action: PayloadAction<DateString>) {
      state.startDate = action.payload;
    },
    setIsSheetOpen(state, action: PayloadAction<boolean>) {
      state.flags.isSheetOpen = action.payload;
    },
    setNavigationReturnDate(
      state,
      action: PayloadAction<DateString | undefined>,
    ) {
      state.navigationReturnDate = action.payload;
    },
    setNavigationState(
      state,
      action: PayloadAction<InstagramScheduleNavigationState>,
    ) {
      state.navigationState = action.payload;
    },
    /**
     * Loads scheduled post info directly into the scheduledPosts store,
     * for use in the export infrastructure as loading/error UI is handled
     * in the export slice separately.
     */
    setScheduledPosts(state, action: PayloadAction<NormalizedScheduledPosts>) {
      state.scheduledPosts = action.payload;
    },
  },
});

export const {
  clearCaptionSearchText,
  setCaptionSearchText,
  setIsSheetOpen,
  setNavigationState,
  setPostFilter,
  setScheduledPosts,
} = actions;

/**
 * Determines the initial view for the calendar based on the current user's
 * last view in user-settings, and determines the appropriate start date +
 * fetches the initial set of scheduled posts for that view.
 */
export const loadInitialScheduleData = createThunk(
  () => async (dispatch, getState) => {
    const settings = await dispatch(fetchInteractiveCalendarSettings());

    const view = settings?.scheduleCalendarView ?? UniversalCalendarView.Week;
    const startOfWeekDay = settings?.scheduleCalendarStartOfWeek ?? 0;

    const tz = getUserTimeZone(getState());
    const today = DateString.getToday(tz);
    const startDate = (() => {
      switch (view) {
        case UniversalCalendarView.Week:
          return getWeekViewStartDate(today, tz, startOfWeekDay);
        case UniversalCalendarView.Month:
          return getMonthViewStartDate(today, tz, startOfWeekDay);
        case UniversalCalendarView.Timeline:
          return today;
      }
    })();

    try {
      const [scheduledPosts] = await Promise.all([
        dispatch(getAndParseAllScheduledItems(startDate)),
        dispatch(instagramScheduleFeedPreviewActions.fetchLivePosts()),
      ]);

      dispatch(
        actions.setInitialScheduleData({ view, scheduledPosts, startDate }),
      );
    } catch (e) {
      dispatch(
        actions.setInitialScheduleData({
          view,
          scheduledPosts: Normalized.empty(),
          startDate,
          fetchScheduledPostsError: getRequestErrorMessage(e),
        }),
      );
    }
  },
);

/**
 * Reschedules the post with the given post id to the given post time,
 * and reloads all scheduled posts from the start date forwards to refresh
 * the calendar for the current view.
 */
export const reschedulePost = createThunk(
  (postId: PostableId, time: Date) => async (dispatch, getState) => {
    const { scheduledPosts } = getState().instagram.schedule;
    const tz = getUserTimeZone(getState());

    const existingPost = Normalized.getById(scheduledPosts, postId);

    // If they attempt to reschedule a post in the past, bail out of the rescheduling process.
    if (time <= new Date()) {
      dispatch(addTimeInPastFailureToast());
      dispatch(navigateBackToInitialDate());
      return;
    }

    // If we don't recognize the post they are rescheduling or they dropped the post
    // in the same time slot as it already was, bail out
    if (!existingPost || +time === +existingPost.scheduledTime) {
      return;
    }

    // To ensure we have smooth drag and drop behavior, we save a temporary copy of the scheduled
    // posts to redux that accounts for the properly rescheduled posts. We then revert this change
    // in the case of failure, and swap it out with the real posts in the case of success.
    const temporaryScheduledPosts = getTemporaryScheduledPosts(
      scheduledPosts,
      postId,
      time,
    );

    dispatch(actions.requestReschedulePost(temporaryScheduledPosts));

    try {
      await universalScheduledPostsApi.reschedulePosts([
        { postableId: postId, scheduledTime: time.getTime() },
      ]);
      dispatch(addReschedulingSuccessToast(momentFromDate(time, tz)));
    } catch (e) {
      dispatch(addReschedulingFailureToast());
      dispatch(
        actions.receiveReschedulePostFailed({
          errorMsg: getRequestErrorMessage(e),
          scheduledPosts,
        }),
      );
      dispatch(navigateBackToInitialDate());
    }

    /**
     * We handle the refetching of posts outside of the try block because we only want
     * to show error UI if the actual post rescheduling fails. The post is already in
     * the correct place if the rescheduling succeeds, so this refetching just ensures
     * our data is up to date.
     */
    const newPosts = await dispatch(getAndParseAllScheduledItems());
    dispatch(actions.receiveReschedulePostSucceeded(newPosts));
  },
);

/**
 * Ensures that we have all necessary posts when changing time views
 * by checking if the new startDate is prior to the old startDate,
 * and fetching all of the posts in between if so.
 */
export const setStartDateAndFetchPosts = createThunk(
  (newStartDate: DateString) => async (dispatch, getState) => {
    const currentStartDate = getCurrentStartDate(getState());
    dispatch(actions.setStartDate(newStartDate));

    const isNewDateFartherInPast =
      DateString.getDayOffset(newStartDate, currentStartDate) > 0;
    if (isNewDateFartherInPast) {
      dispatch(actions.requestScheduledPosts());

      try {
        const currentPosts = getState().instagram.schedule.scheduledPosts;
        const newPosts = await dispatch(
          getAndParseAllScheduledItems(newStartDate, currentStartDate),
        );
        const combinedPosts = mergeScheduledItemLists([currentPosts, newPosts]);

        dispatch(actions.receiveScheduledPostsSucceeded(combinedPosts));
      } catch (e) {
        dispatch(
          actions.receiveScheduledPostsFailed(getRequestErrorMessage(e)),
        );
      }
    }
  },
);

/**
 * Reschedules a group of posts based on the reordering of posts
 * within the FeedPreview. It will create a temporary update to scheduledPosts
 * so that both the FeedPreview and calendar are synchronized and will revert in
 * the case of a failed update. After a success or failure, it will get an updated
 * list of all scheduled items to ensure both the calendar and FeedPreview are up to date.
 */
export const batchReschedulePosts = createThunk(
  (initialIndex: number, newIndex: number) => async (dispatch, getState) => {
    const state = getState();
    const { isFetchingScheduledPosts, isReschedulingPost } =
      state.instagram.schedule.flags;
    const { scheduledPosts } = state.instagram.schedule;
    const scheduledFeedPreviewItems = getFeedPreviewItems(state);

    // No update if we are already in the process of rescheduling or fetching posts
    if (isFetchingScheduledPosts || isReschedulingPost) {
      return;
    }

    // No update if the order was not changed
    if (initialIndex === newIndex) {
      return;
    }

    // Get slice of items that have been reordered
    const itemsToUpdate = scheduledFeedPreviewItems.slice(
      Math.min(initialIndex, newIndex),
      Math.max(initialIndex, newIndex) + 1,
    );

    // Filter to only FeedPreviewItems with scheduledTimes and postIds
    const scheduledItemsToUpdate = itemsToUpdate.filter(
      isScheduledFeedPreviewItem,
    );

    // If the scheduledItemsToUpdate doesn't match the itemsToUpdate it means
    // we got into a bad state. All scheduled items should have
    // postIds and scheduledTimes
    if (itemsToUpdate.length !== scheduledItemsToUpdate.length) {
      dispatch(addBatchReschedulingGenericFailureToast());
      return;
    }

    const postTimes = scheduledItemsToUpdate.map((item) => item.scheduledTime);

    // Check that none of the schedule times are in the past
    const currentTime = +new Date();
    if (postTimes.some((time) => +time <= currentTime)) {
      dispatch(addTimeInPastFailureToast());
      return;
    }

    // Reorder items within slice
    const reorderedItemIds = move(
      scheduledItemsToUpdate,
      newIndex > initialIndex ? 0 : scheduledItemsToUpdate.length - 1,
      newIndex > initialIndex ? scheduledItemsToUpdate.length - 1 : 0,
    ).map((item) => item.postId);

    // Create list of items to update with scheduled times shifted to match order
    const postsToReschedule: ScheduledPostTimeUpdate[] = postTimes
      .map((time, index) => {
        const postId = reorderedItemIds[index];
        if (isDefined(postId)) {
          return {
            postableId: PostableId(postId),
            scheduledTime: time.getTime(),
          };
        }
      })
      .filter(isDefined);

    // To ensure we have smooth drag and drop behavior, we save a temporary copy of the scheduled
    // posts to redux that accounts for the properly rescheduled post. We then revert this change
    // in the case of failure, and swap it out with the real posts in the case of success.
    // This also ensures both FeedPreview and Calendar are synchronized.
    const temporaryScheduledPosts =
      getTemporaryScheduledPostsForBatchReschedule(
        scheduledPosts,
        postsToReschedule,
      );

    dispatch(actions.requestReschedulePost(temporaryScheduledPosts));

    try {
      const rescheduledPosts =
        await universalScheduledPostsApi.reschedulePosts(postsToReschedule);

      dispatch(addBatchReschedulingSuccessToast(rescheduledPosts.length));
    } catch (e) {
      dispatch(addBatchReschedulingGenericFailureToast());
      dispatch(
        actions.receiveReschedulePostFailed({
          errorMsg: getRequestErrorMessage(e),
          scheduledPosts,
        }),
      );
    }

    // Handle the refetching of posts outside of the try block because we only want
    // to show error UI if the actual rescheduling fails. The posts are already in
    // the correct place if the rescheduling succeeds, so this refetching just ensures
    // data is as up to date as possible.
    const allUpdatedPosts = await dispatch(getAndParseAllScheduledItems());
    dispatch(actions.receiveReschedulePostSucceeded(allUpdatedPosts));
  },
);

/**
 * Refreshes the scheduled posts for the current startDate.
 */
export const refreshScheduledPosts = createThunk(
  () => async (dispatch, getState) => {
    const { isFetchingScheduledPosts } = getState().instagram.schedule.flags;

    if (isFetchingScheduledPosts) {
      return;
    }

    dispatch(actions.requestScheduledPosts());

    try {
      const scheduledPosts = await dispatch(getAndParseAllScheduledItems());
      dispatch(actions.receiveScheduledPostsSucceeded(scheduledPosts));
    } catch (e) {
      dispatch(actions.receiveScheduledPostsFailed(getRequestErrorMessage(e)));
    }
  },
);

/**
 * Sets the new view and handles switching the startDate if we are transitioning to the
 * week or month view. If we are switching to the timeline view and we're on the current
 * week, navigate to today.
 */
export const setScheduleViewAndDate = createThunk(
  (view: UniversalCalendarView) => (dispatch, getState) => {
    dispatch(actions.setScheduleView(view));

    /**
     * Save the new view as a user-setting so that a user navigating away from
     * this page will always return to the view they were last on.
     */
    void userSettingsApi.setUserSettings({
      scheduleCalendarView: view,
    });

    const { startOfWeekDay } = getState().instagram.schedule;

    const currentStartDate = getCurrentStartDate(getState());

    switch (view) {
      case UniversalCalendarView.Week:
      case UniversalCalendarView.Month: {
        dispatch(navigateCalendarToDate(currentStartDate));
        return;
      }
      case UniversalCalendarView.Timeline: {
        const tz = getUserTimeZone(getState());
        const todayStartOfWeek = getWeekViewStartDate(
          DateString.getToday(tz),
          tz,
          startOfWeekDay,
        );

        if (DateString.getDayOffset(currentStartDate, todayStartOfWeek) === 0) {
          dispatch(navigateCalendarToToday());
        }

        return;
      }
    }
  },
);

/**
 * Navigates all calendar views forward by the appropriate amount of time.
 */
export const navigateCalendarForward = createThunk(
  () => (dispatch, getState) => {
    const { startDate, startOfWeekDay, view } = getState().instagram.schedule;
    const tz = getUserTimeZone(getState());
    const nextStartDate = getNextStartDate(view, startDate, tz, startOfWeekDay);
    void dispatch(setStartDateAndFetchPosts(nextStartDate));
  },
);

/**
 * Navigates both calendar views backwards by the appropriate amount of time.
 */
export const navigateCalendarBackward = createThunk(
  () => (dispatch, getState) => {
    const { startDate, startOfWeekDay, view } = getState().instagram.schedule;
    const tz = getUserTimeZone(getState());
    const previousStartDate = getPreviousStartDate(
      view,
      startDate,
      tz,
      startOfWeekDay,
    );
    void dispatch(setStartDateAndFetchPosts(previousStartDate));
  },
);

/**
 * Navigates both calendar views to a given date.
 */
export const navigateCalendarToDate = createThunk(
  (date: DateString) => (dispatch, getState) => {
    const tz = getUserTimeZone(getState());
    const { startOfWeekDay, view } = getState().instagram.schedule;

    const targetDate = (() => {
      switch (view) {
        case UniversalCalendarView.Week:
          return getWeekViewStartDate(date, tz, startOfWeekDay);
        case UniversalCalendarView.Month:
          return getMonthViewStartDate(date, tz, startOfWeekDay);
        case UniversalCalendarView.Timeline:
          // TODO: Is this what we want? Or do we want it to start based on the startOfWeekDay week?
          return date;
      }
    })();
    void dispatch(setStartDateAndFetchPosts(targetDate));
  },
);

/**
 * Navigates both calendar views to the current day.
 */
export const navigateCalendarToToday = createThunk(
  () => (dispatch, getState) => {
    const tz = getUserTimeZone(getState());
    const today = DateString.getToday(tz);
    dispatch(navigateCalendarToDate(today));
  },
);

const getScheduledPostSet = async (
  offset: number,
  start: Moment,
  end?: Moment,
) => {
  const requestParams = {
    after: start.toDate(),
    before: end?.toDate(),
    limit: SCHEDULE_FETCH_LIMIT,
    offset,
    status: [
      PostableStatus.Failed,
      PostableStatus.ProcessingComplete,
      PostableStatus.Scheduled,
      PostableStatus.Dispatched,
      PostableStatus.Posted,
      PostableStatus.Processing,
      PostableStatus.Pending,
    ],
    groups: [
      UniversalPostingGroupName.InstagramFeedAutomaticGroup,
      UniversalPostingGroupName.InstagramFeedManualGroup,
      UniversalPostingGroupName.InstagramReelsAutomaticGroup,
      UniversalPostingGroupName.InstagramReelsManualGroup,
      UniversalPostingGroupName.InstagramStoryGroupAutomaticPostingGroup,
      UniversalPostingGroupName.InstagramStoryGroupPostingGroup,
    ],
    userActionStatus: [
      UniversalPostableCustomStatus.NoAction,
      UniversalPostableCustomStatus.PostInitiated,
      UniversalPostableCustomStatus.PostHidden,
    ],
  };

  const { data: posts } =
    await universalScheduledPostsApi.searchScheduledPosts(requestParams);
  const isEndReached = posts.length < SCHEDULE_FETCH_LIMIT;
  const scheduledPosts = posts.filter(isInstagramScheduledPost);
  const associatedMediaById =
    extractAllUniversalScheduledPostMedia(scheduledPosts);

  return {
    associatedMediaById,
    isEndReached,
    scheduledPosts,
  };
};

/**
 * Fetches a complete set of posts for a given date range, or from the start date forwards
 * if no end date is included. If no startDate is included, it uses the current startDate.
 *
 * Loads the associated media items for this post set into
 * the schedule media store, and returns the normalized scheduled posts.
 */
const fetchAllScheduledPosts = createThunk(
  (start: Moment, end?: Moment) => async (dispatch) => {
    let { associatedMediaById, isEndReached, scheduledPosts } =
      await getScheduledPostSet(0, start, end);

    while (!isEndReached) {
      const nextResponse = await getScheduledPostSet(
        scheduledPosts.length,
        start,
        end,
      );
      isEndReached = nextResponse.isEndReached;
      associatedMediaById = {
        ...associatedMediaById,
        ...nextResponse.associatedMediaById,
      };
      scheduledPosts = [...scheduledPosts, ...nextResponse.scheduledPosts];
    }

    dispatch(scheduleMediaActions.updateMediaItems(associatedMediaById));

    return scheduledPosts;
  },
);

export const getAndParseAllScheduledItems = createThunk(
  (startDate?: DateString, endDate?: DateString) =>
    async (dispatch, getState) => {
      const [start, end] = toStartAndEndMoment(getState(), startDate, endDate);
      const posts = await dispatch(fetchAllScheduledPosts(start, end));
      const scheduledPosts = posts.filter(isInstagramScheduledPost);

      const feedPostIds = scheduledPosts
        .filter(
          (post) =>
            isInstagramFeedAutomaticPostConfig(post.config) ||
            isInstagramFeedManualPostConfig(post.config),
        )
        .map((post) => post.id);

      const sortedPosts = sortScheduledPosts(scheduledPosts);

      return normalize(sortedPosts, (post) => post.id);
    },
);

/**
 * Swaps the view to the timeline view and navigates to the date determined by the
 * given offset. This is used to navigate the timeline to a specific day's posts
 * when clicking overflow groups in the week view.
 */
export const navigateToTimelineViewWithOffset = createThunk(
  (dayOffset: number) => (dispatch, getState) => {
    dispatch(actions.setScheduleView(UniversalCalendarView.Timeline));

    const { startDate } = getState().instagram.schedule;
    const tz = getUserTimeZone(getState());

    const newDate = DateString.fromTimestamp(
      getTimezonedMomentFromStartDateOffset(startDate, dayOffset, tz).toDate(),
      tz,
    );

    dispatch(navigateCalendarToDate(newDate));
  },
);

export const fetchInteractiveCalendarSettings = createThunk(
  () => async (dispatch) => {
    dispatch(actions.requestInteractiveCalendarSettings());
    try {
      const { scheduleCalendarStartOfWeek, scheduleCalendarView } =
        await userSettingsApi.getUserSettings();

      const settings = {
        scheduleCalendarStartOfWeek,
        scheduleCalendarView,
      };

      dispatch(actions.receiveInteractiveCalendarSettingsSucceeded(settings));
      return settings;
    } catch (e) {
      dispatch(
        actions.receiveInteractiveCalendarSettingsFailed(
          getRequestErrorMessage(e),
        ),
      );
    }
  },
);

export const setInteractiveCalendarSettings = createThunk(
  (settings: Partial<InteractiveCalendarSettings>) => async (dispatch) => {
    dispatch(actions.requestUpdateInteractiveCalendarSettings());
    try {
      await userSettingsApi.setUserSettings(settings);
      dispatch(actions.receiveUpdateInteractiveCalendarSettingsSucceeded());
      await dispatch(fetchInteractiveCalendarSettings());
    } catch (e) {
      dispatch(
        actions.receiveUpdateInteractiveCalendarSettingsFailed(
          getRequestErrorMessage(e),
        ),
      );
    }
  },
);

export const setNavigationReturnDate = createThunk(
  () => (dispatch, getState) => {
    const currentStartDate = getCurrentStartDate(getState());
    const { view } = getState().instagram.schedule;
    const navigationReturnDate =
      view === UniversalCalendarView.Month
        ? getStartOfCurrentMonth(currentStartDate)
        : currentStartDate;

    dispatch(actions.setNavigationReturnDate(navigationReturnDate));
  },
);

export const navigateBackToInitialDate = createThunk(
  () => (dispatch, getState) => {
    const { navigationReturnDate } = getState().instagram.schedule;
    if (navigationReturnDate) {
      dispatch(navigateCalendarToDate(navigationReturnDate));
    }
  },
);

export default reducer;
